import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { attendanceService } from '@/services/attendance';
import type { AttendanceFlagFilters, ResolveAttendanceFlagInput } from '@/services/attendance/types';
import { useToast } from '@/hooks/use-toast';
import { attendanceKeys } from './useAttendanceSettings';

export const useAttendanceFlags = (
  companyId: string | undefined,
  filters: AttendanceFlagFilters = {},
  pagination: { limit?: number; offset?: number } = {}
) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Note: Polling removed in favor of Realtime subscription (useAttendanceFlagsRealtimeSubscription)
  // This eliminates ~288k-432k API calls/month for 50 users
  const query = useQuery({
    queryKey: [...attendanceKeys.flags(companyId || '', filters), pagination.limit, pagination.offset],
    queryFn: () => attendanceService.getAttendanceFlags(companyId!, filters, pagination),
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000, // Consider data stale after 2 minutes
    refetchOnWindowFocus: true, // Fallback: refetch when user returns to tab
    refetchOnReconnect: true, // Fallback: refetch on network reconnect
  });

  const resolveMutation = useMutation({
    mutationFn: (input: ResolveAttendanceFlagInput) =>
      attendanceService.resolveFlag(input),
    onSuccess: () => {
      if (companyId) {
        queryClient.invalidateQueries({ queryKey: attendanceKeys.flags(companyId, filters) });
      }
      toast({
        title: 'Flag resolved',
        description: 'The attendance flag has been marked as resolved.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to resolve',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const triggerDetectionMutation = useMutation({
    mutationFn: () => attendanceService.triggerMissedPunchDetection(),
    onSuccess: () => {
      if (companyId) {
        queryClient.invalidateQueries({ queryKey: attendanceKeys.flags(companyId, filters) });
      }
      toast({
        title: 'Detection completed',
        description: 'Missed punch detection has been run successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Detection failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    data: query.data?.data,
    totalCount: query.data?.totalCount ?? 0,
    hasMore: query.data?.hasMore ?? false,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    resolveFlag: resolveMutation.mutate,
    isResolving: resolveMutation.isPending,
    triggerDetection: triggerDetectionMutation.mutate,
    isDetecting: triggerDetectionMutation.isPending,
  };
};
